import React, { useState, useRef, useEffect, memo } from "react";

/**
 * SmileyPopup — Microsoft Teams Animated Emojis (PNG)
 * Uses hardcoded direct CDN URLs — no API calls needed.
 * Source: https://github.com/Tarikul-Islam-Anik/Microsoft-Teams-Animated-Emojis
 */

const CDN = 'https://medias.storystudio.cc/Smilies';

const EMOJI_LIST = [
    // Smilies
    { name: 'Grinning Face', download_url: `${CDN}/Grinning%20Face.png` },
    { name: 'Beaming Face', download_url: `${CDN}/Beaming%20Face%20with%20Smiling%20Eyes.png` },
    { name: 'Face with Tears of Joy', download_url: `${CDN}/Face%20with%20Tears%20of%20Joy.png` },
    { name: 'Rolling on the Floor', download_url: `${CDN}/Rolling%20on%20the%20Floor%20Laughing.png` },
    { name: 'Smiling with Heart-Eyes', download_url: `${CDN}/Smiling%20Face%20with%20Heart-Eyes.png` },
    { name: 'Star-Struck', download_url: `${CDN}/Star-Struck.png` },
    { name: 'Face Blowing a Kiss', download_url: `${CDN}/Face%20Blowing%20a%20Kiss.png` },
    { name: 'Smiling Face with Sunglasses', download_url: `${CDN}/Smiling%20Face%20with%20Sunglasses.png` },
    { name: 'Thinking Face', download_url: `${CDN}/Thinking%20Face.png` },
    { name: 'Face with Raised Eyebrow', download_url: `${CDN}/Face%20with%20Raised%20Eyebrow.png` },
    { name: 'Winking Face', download_url: `${CDN}/Winking%20Face.png` },
    { name: 'Smirking Face', download_url: `${CDN}/Smirking%20Face.png` },
    { name: 'Zany Face', download_url: `${CDN}/Zany%20Face.png` },
    { name: 'Face with Open Mouth', download_url: `${CDN}/Face%20with%20Open%20Mouth.png` },
    { name: 'Hushed Face', download_url: `${CDN}/Hushed%20Face.png` },
    { name: 'Exploding Head', download_url: `${CDN}/Exploding%20Head.png` },
    { name: 'Loudly Crying Face', download_url: `${CDN}/Loudly%20Crying%20Face.png` },
    { name: 'Angry Face', download_url: `${CDN}/Angry%20Face.png` },
    { name: 'Partying Face', download_url: `${CDN}/Partying%20Face.png` },
    { name: 'Shushing Face', download_url: `${CDN}/Shushing%20Face.png` },
    // Hand gestures
    { name: 'Thumbs Up', download_url: `${CDN}/Thumbs%20Up.png` },
    { name: 'Thumbs Down', download_url: `${CDN}/Thumbs%20Down.png` },
    { name: 'Clapping Hands', download_url: `${CDN}/Clapping%20Hands.png` },
    { name: 'Raising Hands', download_url: `${CDN}/Raising%20Hands.png` },
    { name: 'Flexed Biceps', download_url: `${CDN}/Flexed%20Biceps.png` },
    { name: 'Waving Hand', download_url: `${CDN}/Waving%20Hand.png` },
    { name: 'OK Hand', download_url: `${CDN}/OK%20Hand.png` },
    { name: 'Victory Hand', download_url: `${CDN}/Victory%20Hand.png` },
    { name: 'Crossed Fingers', download_url: `${CDN}/Crossed%20Fingers.png` },
    { name: 'Love-You Gesture', download_url: `${CDN}/Love-You%20Gesture.png` },
    // Symbols & Objects
    { name: 'Red Heart', download_url: `${CDN}/Red%20Heart.png` },
    { name: 'Fire', download_url: `${CDN}/Fire.png` },
    { name: 'Star', download_url: `${CDN}/Star.png` },
    { name: 'Sparkles', download_url: `${CDN}/Sparkles.png` },
    { name: 'Party Popper', download_url: `${CDN}/Party%20Popper.png` },
    { name: 'Rocket', download_url: `${CDN}/Rocket.png` },
    { name: 'Trophy', download_url: `${CDN}/Trophy.png` },
    { name: 'Crown', download_url: `${CDN}/Crown.png` },
    { name: 'Light Bulb', download_url: `${CDN}/Light%20Bulb.png` },
    { name: 'Money with Wings', download_url: `${CDN}/Money%20with%20Wings.png` },
];

/** Individual emoji tile with its own loading state */
const EmojiTile = memo(({ emoji, onSelect, onClose }: {
    emoji: typeof EMOJI_LIST[0];
    onSelect: (smiley: any) => void;
    onClose: () => void;
}) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    if (error) return null; // Hide broken emojis entirely

    return (
        <button
            onClick={() => { onSelect(emoji); onClose(); }}
            className="aspect-square p-1 rounded-md hover:bg-gray-800 transition-colors flex items-center justify-center"
            title={emoji.name}
        >
            {!loaded && (
                <div className="w-10 h-10 rounded-md bg-gray-700 animate-pulse" />
            )}
            <img
                src={emoji.download_url}
                alt={emoji.name}
                width="40"
                height="40"
                loading="lazy"
                className={`object-contain transition-opacity ${loaded ? 'opacity-100' : 'opacity-0 absolute'}`}
                onLoad={() => setLoaded(true)}
                onError={() => setError(true)}
            />
        </button>
    );
});

export const SmileyPopup = memo(({
    isOpen,
    onClose,
    onSelect
}: {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (smiley: any) => void;
}) => {
    const popupRef = useRef<HTMLDivElement>(null);
    const [filter, setFilter] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !(popupRef.current as any).contains(event.target as any)) {
                onClose();
            }
        };
        window.document.addEventListener("mousedown", handleClickOutside);
        return () => window.document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const filtered = filter
        ? EMOJI_LIST.filter(e => e.name.toLowerCase().includes(filter.toLowerCase()))
        : EMOJI_LIST;

    return (
        <div ref={popupRef} className="absolute bottom-full mb-2 right-0 w-80 h-96 bg-gray-900 border border-gray-800 rounded-xl shadow-lg z-50 flex flex-col">
            <div className="p-2 border-b border-gray-800">
                <input
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Search..."
                    className="w-full bg-gray-800 text-white text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-500"
                    autoFocus
                />
            </div>
            <div className="flex-1 overflow-y-auto p-2">
                <div className="grid grid-cols-5 gap-2">
                    {filtered.map(smiley => (
                        <EmojiTile
                            key={smiley.name}
                            emoji={smiley}
                            onSelect={onSelect}
                            onClose={onClose}
                        />
                    ))}
                </div>
                {filtered.length === 0 && (
                    <p className="text-xs text-gray-500 text-center py-4">Aucun résultat</p>
                )}
            </div>
        </div>
    );
});
